/**
 * SOURCE / PRIORITY / STATUS -- one vocabulary for the queue page and the
 * console rail, so the same issue never reads differently in the two places.
 *
 * Each helper returns a `tone`, not a class: the queue is ruby-on-light and the
 * console is GitHub-dark, so each surface maps the tone to its own colours.
 * No purple / violet / indigo / fuchsia anywhere (rubytill-theme).
 */
import { DEVICE_AUTO_CATEGORY as SHARED_AUTO_CATEGORY, DEVICE_AUTO_CATEGORY_LABEL as SHARED_AUTO_LABEL } from '@platform/shared-types';

/*
  DEVICE-HEALTH AUTO TICKETS (spec 2026-10-04). Runtime shared-types values,
  read defensively: a stale Vite pre-bundle hands back `undefined` at module
  scope, which would white-screen the queue AND the console. Degrade to the
  known literal and say what to do.
*/
const readShared = (v: unknown, name: string, fallback: string): string => {
  if (typeof v === 'string' && v) return v;
  console.error(`@platform/shared-types exported no ${name} — a stale Vite pre-bundle. RESTART the dev server: deleting node_modules/.vite under a running server does nothing.`);
  return fallback;
};
export const DEVICE_AUTO_CATEGORY = readShared(SHARED_AUTO_CATEGORY, 'DEVICE_AUTO_CATEGORY', 'device_auto');
export const DEVICE_AUTO_LABEL = readShared(SHARED_AUTO_LABEL, 'DEVICE_AUTO_CATEGORY_LABEL', 'Android devices · POS auto');

export type MetaTone = 'ruby' | 'rubySoft' | 'amber' | 'green' | 'sky' | 'slate';

export type SourceKind = 'pos-auto' | 'pos-terminal' | 'admin' | 'portal' | 'advertising' | 'web';

/*
  Where a report came from. Prefer the explicit fields the reporters now set
  (issue.source = 'pos-terminal' from the till SPA; context.source =
  'admin-panel' from super-admin-ui), then fall back to route heuristics so
  older reports -- which carry neither field -- still get a sensible label.
*/
export const deriveSource = (iss: any): { kind: SourceKind; label: string } => {
  const route: string = iss?.context?.route || '';
  const ctxSource: string = iss?.context?.source || '';
  const productId: string = iss?.context?.productId || '';

  if (iss?.source === 'pos-auto') return { kind: 'pos-auto', label: 'POS auto' };
  if (iss?.source === 'pos-terminal') return { kind: 'pos-terminal', label: 'POS Terminal' };
  if (ctxSource === 'admin-panel' || route.startsWith('/super-admin')) return { kind: 'admin', label: 'Admin Panel' };
  if (route.startsWith('/portal')) return { kind: 'portal', label: 'Employee Portal' };
  if (route.startsWith('/pos/advertising')) return { kind: 'advertising', label: 'Advertising' };
  // platform-app. For AgenticPOS the web app *is* the POS back-office.
  return { kind: 'web', label: productId === 'agenticpos' ? 'POS Web' : 'Web App' };
};

export const PRIORITY_META: Record<string, { label: string; tone: MetaTone }> = {
  critical: { label: 'Critical', tone: 'ruby' },
  blocking: { label: 'Blocking', tone: 'rubySoft' },
  annoying: { label: 'Annoying', tone: 'amber' },
  minor:    { label: 'Minor',    tone: 'slate' },
};

export const priorityMeta = (p?: string) =>
  PRIORITY_META[p || ''] || { label: p || 'No priority', tone: 'slate' as MetaTone };

export const STATUS_META: Record<string, { label: string; tone: MetaTone }> = {
  new:                     { label: 'New',               tone: 'sky' },
  in_progress:             { label: 'In Progress',       tone: 'amber' },
  deploying:               { label: 'Deploying',         tone: 'amber' },
  awaiting_retest:         { label: 'Awaiting Retest',   tone: 'green' },
  awaiting_owner_approval: { label: 'Awaiting Approval', tone: 'rubySoft' },
  resolved:                { label: 'Resolved',          tone: 'slate' },
  rejected:                { label: 'Rejected',          tone: 'ruby' },
};

export const statusMeta = (s?: string) =>
  STATUS_META[s || ''] || { label: s || 'Unknown', tone: 'slate' as MetaTone };

/*
  THE CONSOLE OPENS IN ITS OWN TAB, so a verdict given there has to reach the
  queue tab without anyone reloading it. A BroadcastChannel ping says "the set
  changed"; the queue refreshes in the background, same as a tab switch.
  Wrapped: a browser without BroadcastChannel still has the focus refresh.
*/
export const ISSUE_CHANNEL = 'rt-issue-console';

export const notifyQueue = (): void => {
  try {
    const c = new BroadcastChannel(ISSUE_CHANNEL);
    c.postMessage({ type: 'changed' });
    c.close();
  } catch { /* no channel -- the queue's focus refresh covers it */ }
};

/** What an auto ticket says about itself: how often, how recently, which tills. Null for human tickets. */
export interface AutoTicketFacts { count: number; lastSeenAt: number | null; devices: Array<{ deviceId: string; label: string; count: number }> }

export const autoTicketFacts = (iss: any): AutoTicketFacts | null => {
  if (iss?.category !== DEVICE_AUTO_CATEGORY) return null;
  const devices = Array.isArray(iss.affectedDevices) ? iss.affectedDevices : [];
  return {
    count: Number(iss.occurrenceCount) || 0,
    lastSeenAt: typeof iss.lastSeenAt === 'number' ? iss.lastSeenAt : null,
    devices: devices.slice(0, 6).map((d: any) => ({
      deviceId: String(d.deviceId), label: d.registerName || String(d.deviceId), count: Number(d.count) || 0,
    })),
  };
};
